import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import Header from './Header';
import Pagination from './Pagination';
import {
  Landmark,
  Eye,
  Download,
  Search,
  Filter,
  DollarSign,
  MapPin,
  BarChart3,
  TrendingUp,
  Percent
} from 'lucide-react';

const ModalViewPlots = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;

  let userCompanyID = localStorage.getItem('companyID')
  if(userCompanyID === 'Admin'){
   userCompanyID = 1;
  }
  userCompanyID = JSON.parse(userCompanyID)

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const formatCurrencyTable = (amount) => {
    const number = typeof amount === 'number' ? amount : parseFloat(amount);
    return new Intl.NumberFormat('en-ZM', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2}).format(number);
  };

  const [plotsData, setPlotsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDataForTablePlotsCustomers();
  }, []);

  useEffect(() => {
    // Filter data whenever search value changes
    if (searchValue.trim() === '') {
      setFilteredData(plotsData);
    } else {
      const filtered = plotsData.filter(plot =>
        Object.values(plot).some(value =>
          value?.toString().toLowerCase().includes(searchValue.toLowerCase())
        )
      );
      setFilteredData(filtered);
    }
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchValue, plotsData]);

  const fetchDataForTablePlotsCustomers = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("apiType", 'viewPlots');
    formData.append('userCompanyID', userCompanyID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setPlotsData(response.data);
      setFilteredData(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching data:', error);
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    const tableRows = [];

    filteredData.forEach((plot, index) => {
      tableRows.push([
        index + 1,
        plot.businessLocationName,
        plot.plotNumber,
        plot.plotNumbers,
        formatCurrencyTable(plot.plotPrice),
        formatCurrencyTable(plot.plotPrices),
        formatCurrencyTable(plot.monthlyInstallments),
        formatCurrencyTable(plot.twentyPercentDeposit),
        formatCurrencyTable(plot.paidToNHEF),
        formatCurrencyTable(plot.totalAmountPaid),
        plot.contributionsFrequency,
        plot.plotStatus
      ]);
    });

    doc.autoTable({
      head: [['SN', 'Location', 'Plot #', 'New Plot #', 'Price', 'New Price', 'Monthly', '20% Deposit', 'NHEF Paid', 'Total Paid', 'Frequency', 'Status']],
      body: tableRows,
      margin: { top: 20 },
      styles: {
        fontSize: 7,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      didDrawPage: (data) => {
        doc.setFontSize(10);
        doc.text(`Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
      }
    });

    doc.save('plots-report.pdf');
  };

  const calculateTotals = (data) => {
    const totals = data.reduce((acc, plot) => {
      acc.plotPrice += parseFloat(plot.plotPrice) || 0;
      acc.plotPricesNew += parseFloat(plot.plotPricesNew) || 0;
      acc.paidToNHEF += parseFloat(plot.paidToNHEF) || 0;
      acc.totalAmountPaid += parseFloat(plot.totalAmountPaid) || 0;
      acc.monthlyInstallments += parseFloat(plot.monthlyInstallments) || 0;
      acc.twentyPercentDeposit += parseFloat(plot.twentyPercentDeposit) || 0;
      acc.totalPlots += 1;
      acc.soldPlots += plot.plotStatus === 'Sold' ? 1 : 0;
      acc.availablePlots += plot.plotStatus === 'Available' ? 1 : 0;
      return acc;
    }, { 
      plotPrice: 0, 
      plotPricesNew: 0, 
      paidToNHEF: 0, 
      totalAmountPaid: 0, 
      monthlyInstallments: 0, 
      twentyPercentDeposit: 0,
      totalPlots: 0,
      soldPlots: 0,
      availablePlots: 0
    });
    
    return totals;
  };

  // Calculate pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  const totals = calculateTotals(plotsData);
  const filteredTotals = calculateTotals(filteredData);

  return (
    <>
      <Header />
      <main className="lg:ml-64 pt-16 min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
                    <Landmark className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">View Plots</h1>
                    <p className="text-gray-600">Manage and view all plot properties</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleExportPDF}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export PDF</span>
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Plots</p>
                      <p className="text-2xl font-bold text-gray-900">{totals.totalPlots}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${totals.soldPlots > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {totals.soldPlots} sold
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${totals.availablePlots > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {totals.availablePlots} available
                        </span>
                      </div>
                    </div>
                    <Landmark className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Plot Value</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.plotPrice)}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatCurrency(totals.plotPricesNew)} new price</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Paid</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalAmountPaid)}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Percent className="w-4 h-4 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          20%: {formatCurrency(totals.twentyPercentDeposit)}
                        </span>
                      </div>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">NHEF Contributions</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.paidToNHEF)}</p>
                      <p className="text-xs text-gray-500 mt-1">Monthly: {formatCurrency(totals.monthlyInstallments)}</p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-orange-500" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search plots..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white w-64"
                      />
                    </div>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                      className="px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5 entries</option>
                      <option value="10">10 entries</option>
                      <option value="25">25 entries</option>
                      <option value="50">50 entries</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      Showing {filteredData.length} of {plotsData.length} plots
                      {searchValue && ` (filtered)`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="mt-4 text-gray-500">Loading plots data...</p>
                  </div>
                ) : (
                  <>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-green-500 to-emerald-600">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">SN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            Location
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Plot #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">New Plot #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Monthly</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">20% Deposit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">NHEF</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Total Paid</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Frequency</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentRows.map((plot, index) => (
                          <tr key={plot.plotsID} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{indexOfFirstRow + index + 1}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                                {plot.businessLocationName}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                              {plot.plotNumber}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {plot.businessLocationID === "3" ? plot.plotNumber : plot.plotNumbers}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {formatCurrency(plot.plotPrice)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(plot.monthlyInstallments)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(plot.twentyPercentDeposit)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(plot.paidToNHEF)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                              {formatCurrency(plot.totalAmountPaid)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                {plot.contributionsFrequency}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                plot.plotStatus === 'Sold' ? 'bg-green-100 text-green-800' :
                                plot.plotStatus === 'Available' ? 'bg-blue-100 text-blue-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {plot.plotStatus}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="4" className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                            Filtered Totals:
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.plotPrice)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.monthlyInstallments)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.twentyPercentDeposit)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.paidToNHEF)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.totalAmountPaid)}
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>

                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      totalItems={filteredData.length}
                    />
                  </>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div>
                    Page {currentPage} of {totalPages} • {filteredData.length} plots
                    {searchValue && ' (filtered)'}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleExportPDF}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg hover:opacity-90 transition-all"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download PDF</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalViewPlots;